import { useState, useEffect, useRef } from "react";

interface Dimension {
  label: string;
  score: number;
  max: number;
}

interface PreviewData {
  title: string;
  icon: string;
  accent: string;
  url: string;
  score: number;
  grade: string;
  reifegrad: string;
  dimensions: Dimension[];
  topTipp: { kriterium: string; massnahme: string };
  href: string;
}

function AnimatedBar({ score, max, color, delay }: { score: number; max: number; color: string; delay: number }) {
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const t = setTimeout(() => setWidth((score / max) * 100), delay);
          return () => clearTimeout(t);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [score, max, delay]);

  return (
    <div ref={ref} className="h-1.5 bg-gray-100">
      <div
        className="h-full"
        style={{
          width: `${width}%`,
          backgroundColor: color,
          transition: "width 1.2s ease-out",
        }}
      />
    </div>
  );
}

function AnimatedScore({ target, color }: { target: number; color: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const step = target / (1600 / 16);
          const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(Math.floor(start));
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref} style={{ color }}>{count}</span>;
}

export function AnalysisPreviewCard({ data }: { data: PreviewData }) {
  const scoreColor = data.score >= 70 ? "#115751" : data.score >= 45 ? "#b45309" : data.accent;

  return (
    <div className="border border-gray-200 bg-white overflow-hidden flex flex-col">
      {/* Farbstreifen */}
      <div className="h-1" style={{ backgroundColor: data.accent }} />

      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between">
        <div>
          <p className="text-xs font-bold tracking-widest uppercase mb-0.5" style={{ color: data.accent, fontFamily: "'DM Sans', sans-serif" }}>
            <img src={data.icon} alt="" className="inline-block w-4 h-4 mr-1 object-contain align-middle" style={{ opacity: 0.85 }} /> Beispiel-Ausschnitt
          </p>
          <h3 className="font-black uppercase leading-none text-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.4rem" }}>
            {data.title}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5 truncate" style={{ fontFamily: "'DM Sans', sans-serif", maxWidth: "200px" }}>
            {data.url}
          </p>
        </div>
        {/* Score-Kreis */}
        <div
          className="flex flex-col items-center justify-center border-2 w-16 h-16 flex-shrink-0"
          style={{ borderColor: scoreColor }}
        >
          <span className="font-black text-2xl leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            <AnimatedScore target={data.score} color={scoreColor} />
          </span>

        </div>
      </div>

      {/* Reifegrad-Badge */}
      <div className="px-5 mb-3">
        <span
          className="inline-block text-xs font-bold px-2 py-0.5"
          style={{ backgroundColor: `${scoreColor}15`, color: scoreColor, fontFamily: "'DM Sans', sans-serif" }}
        >
          {data.reifegrad}
        </span>
      </div>

      {/* Dimensionen */}
      <div className="px-5 pb-4 space-y-2.5">
        {data.dimensions.map((dim, i) => (
          <div key={dim.label}>
            <div className="flex justify-between text-xs mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <span className="text-gray-600">{dim.label}</span>
              <span className="font-bold" style={{ color: data.accent }}>{dim.score}/{dim.max}</span>
            </div>
            <AnimatedBar score={dim.score} max={dim.max} color={data.accent} delay={i * 150} />
          </div>
        ))}
      </div>

      {/* Top-Tipp (teilweise geschwärzt) */}
      <div className="mx-5 mb-4 p-3 border border-dashed" style={{ borderColor: `${data.accent}40`, backgroundColor: `${data.accent}06` }}>
        <p className="text-xs font-bold mb-1" style={{ color: data.accent, fontFamily: "'DM Sans', sans-serif" }}>
          <img src="/manus-storage/icon_cta_value_9a48b0a2.png" alt="" className="inline-block w-3.5 h-3.5 mr-1 object-contain align-middle" style={{ opacity: 0.85 }} /> Wichtigster Optimierungspunkt
        </p>
        <p className="text-xs font-medium text-gray-700 mb-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {data.topTipp.kriterium}
        </p>
        {/* Geschwärzter Tipp-Text */}
        <div className="relative">
          <p className="text-xs text-gray-500 blur-[3px] select-none" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {data.topTipp.massnahme}
          </p>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-bold px-2 py-0.5 text-white" style={{ backgroundColor: data.accent, fontFamily: "'DM Sans', sans-serif" }}>
              Im vollständigen Report →
            </span>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-auto px-5 pb-5">
        <a href={data.href}>
          <button
            className="w-full py-3 text-xs font-bold uppercase tracking-widest text-white"
            style={{ backgroundColor: data.accent, fontFamily: "'DM Sans', sans-serif" }}
          >
            Eigene Website analysieren →
          </button>
        </a>
      </div>
    </div>
  );
}

export const PREVIEW_DATA: PreviewData[] = [
  {
    title: "Sensorik-Barometer™",
    icon: "/manus-storage/11_094992a3.svg",
    accent: "#6d0000",
    url: "beispiel-shop.de",
    score: 58,
    grade: "C+",
    reifegrad: "Sensorisch unterentwickelt",
    dimensions: [
      { label: "Visuelle Sensorik", score: 13, max: 20 },
      { label: "Sprache & Emotion", score: 11, max: 20 },
      { label: "Auditive Sensorik", score: 6, max: 15 },
      { label: "Haptik & Interaktion", score: 14, max: 20 },
      { label: "Assoziative Sensorik", score: 8, max: 15 },
      { label: "Vertrauen & Trust", score: 6, max: 10 },
    ],
    topTipp: {
      kriterium: "Sensorische Sprache fehlt",
      massnahme: "Ersetze abstrakte Produktbeschreibungen durch sensorische Wörter wie 'samtig', 'knackig', 'strahlend'. Studien zeigen 34 % höhere Verweildauer bei sensorischer Sprache.",
    },
    href: "/sensorik",
  },
  {
    title: "Lokal-Barometer™",
    icon: "/manus-storage/1_55991aa3.svg",
    accent: "#115751",
    url: "muster-handwerk.de",
    score: 44,
    grade: "D+",
    reifegrad: "Lokal kaum sichtbar",
    dimensions: [
      { label: "Google Business Profile", score: 8, max: 20 },
      { label: "NAP-Konsistenz", score: 6, max: 15 },
      { label: "Lokale Keywords", score: 10, max: 20 },
      { label: "Bewertungen", score: 9, max: 15 },
      { label: "Lokale Backlinks", score: 5, max: 10 },
      { label: "Branchenverzeichnisse", score: 6, max: 20 },
    ],
    topTipp: {
      kriterium: "Google Business Profile unvollständig",
      massnahme: "Ergänze alle Öffnungszeiten, Fotos (min. 10), Leistungen und eine vollständige Beschreibung. Unvollständige Profile verlieren bis zu 60 % der lokalen Klicks.",
    },
    href: "/lokal",
  },
  {
    title: "GEO-Barometer™",
    icon: "/manus-storage/7_8bcad2d2.svg",
    accent: "#1a3a5c",
    url: "beratung-beispiel.de",
    score: 51,
    grade: "C",
    reifegrad: "KI-Sichtbarkeit ausbaufähig",
    dimensions: [
      { label: "Strukturierte Daten", score: 9, max: 20 },
      { label: "E-E-A-T Signale", score: 10, max: 20 },
      { label: "KI-Zitierbarkeit", score: 8, max: 20 },
      { label: "Autorität & Vertrauen", score: 12, max: 20 },
      { label: "Content-Tiefe", score: 12, max: 20 },
    ],
    topTipp: {
      kriterium: "Strukturierte Daten fehlen",
      massnahme: "Implementiere Schema.org-Markup für Organization, LocalBusiness und FAQPage. ChatGPT und Perplexity bevorzugen Seiten mit strukturierten Daten um Faktor 2,3.",
    },
    href: "/geo-barometer",
  },
  {
    title: "SEO-Barometer™",
    icon: "/manus-storage/23_1ab7147e.svg",
    accent: "#0a5c36",
    url: "online-shop-demo.de",
    score: 62,
    grade: "B-",
    reifegrad: "SEO-Grundlagen vorhanden",
    dimensions: [
      { label: "On-Page Grundlagen", score: 17, max: 25 },
      { label: "Technische SEO", score: 13, max: 20 },
      { label: "Ladegeschwindigkeit", score: 11, max: 20 },
      { label: "Inhaltsqualität", score: 14, max: 25 },
      { label: "E-E-A-T Signale", score: 7, max: 10 },
    ],
    topTipp: {
      kriterium: "Core Web Vitals: LCP zu langsam",
      massnahme: "Der Largest Contentful Paint liegt bei 4,2 s (Ziel: unter 2,5 s). Komprimiere Hero-Bilder auf WebP, aktiviere Lazy Loading und nutze ein CDN.",
    },
    href: "/seo-barometer",
  },
  {
    title: "Conversion-Barometer™",
    icon: "/manus-storage/33_d51b7819.svg",
    accent: "#b45309",
    url: "dienstleister-muster.de",
    score: 55,
    grade: "C+",
    reifegrad: "Conversion-Potenzial ungenutzt",
    dimensions: [
      { label: "Persuasion & Psychologie", score: 11, max: 20 },
      { label: "Vertrauen & Trust", score: 12, max: 20 },
      { label: "CTA & Value Proposition", score: 10, max: 20 },
      { label: "Reibungsfreiheit", score: 13, max: 25 },
      { label: "Mobile Conversion", score: 9, max: 15 },
    ],
    topTipp: {
      kriterium: "CTA zu generisch",
      massnahme: "Ersetze 'Kontakt aufnehmen' durch spezifische Handlungsaufforderungen wie 'Kostenlose Erstberatung sichern'. Spezifische CTAs steigern die Klickrate um bis zu 202 %.",
    },
    href: "/conversion-barometer",
  },
  {
    title: "Accessibility-Barometer™",
    icon: "/manus-storage/16_9c3f7718.svg",
    accent: "#3730a3",
    url: "restaurant-beispiel.de",
    score: 48,
    grade: "D+",
    reifegrad: "Barrierefreiheit mangelhaft",
    dimensions: [
      { label: "Wahrnehmbarkeit", score: 12, max: 25 },
      { label: "Bedienbarkeit", score: 10, max: 20 },
      { label: "Verständlichkeit", score: 11, max: 20 },
      { label: "Robustheit", score: 7, max: 15 },
      { label: "EAA-Compliance", score: 8, max: 20 },
    ],
    topTipp: {
      kriterium: "Farbkontraste unzureichend",
      massnahme: "7 von 12 Textelementen unterschreiten das WCAG-Mindestkontrastverh. von 4,5:1. Betrifft besonders graue Texte auf weißem Hintergrund – Bußgeld-Risiko ab 28.06.2025.",
    },
    href: "/accessibility-barometer",
  },
  {
    title: "Datenschutz-Barometer™",
    icon: "/manus-storage/29_7f3bb394.svg",
    accent: "#0f766e",
    url: "praxis-muster.de",
    score: 53,
    grade: "C",
    reifegrad: "DSGVO-Risiken vorhanden",
    dimensions: [
      { label: "Cookie-Consent", score: 9, max: 20 },
      { label: "Impressum & Pflichtangaben", score: 13, max: 20 },
      { label: "Datenschutzerklärung", score: 10, max: 20 },
      { label: "SSL & Sicherheit", score: 14, max: 20 },
      { label: "Externe Dienste", score: 7, max: 20 },
    ],
    topTipp: {
      kriterium: "Google Fonts ohne Einwilligung geladen",
      massnahme: "Google Fonts werden direkt von Google-Servern geladen und übermitteln IP-Adressen ohne Einwilligung. Lade Fonts lokal oder nutze einen DSGVO-konformen Proxy.",
    },
    href: "/datenschutz-barometer",
  },
];
